// The identity route contract — standardized across every app so the login and
// linking flows land at the same places everywhere. Each surface's UI package
// binds its screen components to these paths; this module owns only the paths
// and the boot-outcome → landing mapping, both surface-agnostic.
//
// The one genuinely app-specific bit — where a signed-in Human lands AFTER
// identity (a mobile dashboard vs a clerk console home) — is deliberately NOT
// here. The app supplies that target for the app-exit outcomes.

export const IDENTITY_PATHS = Object.freeze({
  login: '/login',
  accountNotLinked: '/account-not-linked',
  welcome: '/welcome',
  signInSecurity: '/sign-in-security',
  // The acknowledgement page a link round trip ("Add a login") lands on: the
  // verdict in plain words and one way back to Sign-in & security. A page, not
  // a query on Sign-in & security, because a person coming back from a trip off
  // the app needs a verdict that stays on screen (identity effort ruling 2026-09-21: identity screens show verdicts on-screen, never a toast).
  linkOutcome: '/link-outcome'
});

// Map a completed boot-auth result to its identity landing. Returns { path,
// query? } for the identity-internal outcomes (the ones that must look the same
// in every app) — the choice screens, and the link-outcome acknowledgement a
// link round trip ends on, whether it came back as its own 'linked' /
// 'link-failed' outcome or riding an 'authenticated' login that completed a
// parked connect. Returns null for the app-exit outcomes (a plain
// 'authenticated', 'no-callback', 'failed') — those the app routes to its own
// home or retry target.
export function routeForBootOutcome(bootAuth) {
  switch (bootAuth?.outcome) {
    case 'linked':
      return routeForLinkOutcome({ linked: true });
    case 'link-failed':
      return routeForLinkOutcome({ error: bootAuth.error });
    case 'authenticated':
      if (bootAuth.linked) return routeForLinkOutcome({ linked: true });
      if (bootAuth.linkError) return routeForLinkOutcome({ error: bootAuth.linkError });
      return null;
    case 'unlinked':
      return { path: IDENTITY_PATHS.accountNotLinked };
    case 'match-found':
      return {
        path: IDENTITY_PATHS.accountNotLinked,
        query: { mode: 'match', providers: (bootAuth.providers || []).join(',') }
      };
    case 'restorable':
      return { path: IDENTITY_PATHS.accountNotLinked, query: { mode: 'restorable' } };
    default:
      return null;
  }
}

// The link-outcome page's query: `outcome=linked`, or `outcome=refused` with the
// server's `reason` (CONNECTION_IN_USE, ATTACH_FAILED_…, a provider error). The
// page reads exactly these two keys.
function routeForLinkOutcome({ linked = false, error = '' } = {}) {
  return {
    path: IDENTITY_PATHS.linkOutcome,
    query: linked ? { outcome: 'linked' } : { outcome: 'refused', reason: String(error || '') }
  };
}
